import QueryString from "query-string";
import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  getNextOfKinSuccess,
  getNextOfKinFailure,
  saveNextOfKinSuccess,
  saveNextOfKinFailure,
  fetchOtherContactsSuccess,
  fetchOtherContactsFailure,
  addOtherContactSuccess,
  addOtherContactFailure,
  editOtherContactSuccess,
  editOtherContactFailure,
  deleteOtherContactSuccess,
  deleteOtherContactFailure,
  removeContactSuccess,
  removeContactFailure,
  searchContactsSuccess,
  searchContactsFailure,
  getContactSuggestionsSuccess,
  getContactSuggestionsFailure,
  sendFamilyInviteSuccess,
  sendFamilyInviteFailure,
  resendFamilyInviteSuccess,
  resendFamilyInviteFailure,
  revokeFamilyAccessSuccess,
  revokeFamilyAccessFailure,
  toggleFamilyInviteModal,
  fetchFamilyAccessSuccess,
  fetchFamilyAccessFailure,
  fetchFamilyModulePermissionsFailure,
  fetchFamilyModulePermissionsSuccess,
  saveFamilyModuleOverridesFailure,
  saveFamilyModuleOverridesSuccess,
} from "./actions";
import {
  GET_NEXT_OF_KIN,
  SAVE_NEXT_OF_KIN,
  FETCH_OTHER_CONTACTS,
  ADD_OTHER_CONTACT,
  EDIT_OTHER_CONTACT,
  DELETE_OTHER_CONTACT,
  REMOVE_CONTACT,
  SEARCH_CONTACTS,
  GET_CONTACT_SUGGESTIONS,
  SEND_FAMILY_INVITE,
  RESEND_FAMILY_INVITE,
  REVOKE_FAMILY_ACCESS,
  FETCH_FAMILY_ACCESS,
  FETCH_FAMILY_MODULE_PERMISSIONS,
  SAVE_FAMILY_MODULE_OVERRIDES,
} from "./constants";

export function* fetchNextOfKinSaga({ payload, onGet }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(api(token).get, `/api/next-of-kin/${payload}`);
    const formattedData = {
      primaryContact:
        data?.find((item) => item?.contact_type === "primary") ?? {},
      secondaryContact:
        data?.find((item) => item?.contact_type === "secondary") ?? {},
    };
    yield put(getNextOfKinSuccess(formattedData));
    typeof onGet === "function" && onGet(formattedData);
  } catch (e) {
    yield put(getNextOfKinFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching residence!"));
  }
}

export function* saveNextOfKinSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, formData } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/update-next-of-kin/${residentId}`,
      formData,
    );
    yield put(saveNextOfKinSuccess(data));
  } catch (e) {
    yield put(saveNextOfKinFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching residence!"));
  }
}

/** ResourceCollection + LengthAwarePaginator can nest rows at data.data.data. */
const normalizeOtherContactsPage = (data) => {
  if (Array.isArray(data)) {
    return {
      data,
      total: data.length,
      current_page: 1,
      last_page: 1,
    };
  }
  const rows = Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data?.data?.data)
      ? data.data.data
      : [];
  return {
    ...data,
    data: rows,
    current_page: data?.current_page ?? 1,
    last_page: data?.last_page ?? 1,
    total: data?.total ?? rows.length,
  };
};

export function* fetchOtherContactsSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = payload?.data ? QueryString.stringify(payload.data) : "";
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident-contact?${query}`,
    );
    yield put(fetchOtherContactsSuccess(normalizeOtherContactsPage(data)));
  } catch (e) {
    yield put(fetchOtherContactsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching other contacts!"));
  }
}

export function* addOtherContactSaga({ payload, onAdd, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident-contact/add`,
      payload,
    );
    yield put(addOtherContactSuccess(data));
    typeof onAdd === "function" && onAdd(data);
  } catch (e) {
    yield put(addOtherContactFailure());
    typeof onError === "function" && onError(e);
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while adding other contact!"));
  }
}

export function* editOtherContactSaga({ payload, onEdit, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { id, formData } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident-contact/edit/${id}`,
      formData,
    );
    yield put(editOtherContactSuccess(data));
    typeof onEdit === "function" && onEdit(data);
  } catch (e) {
    yield put(editOtherContactFailure());
    typeof onError === "function" && onError(e);
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while editing other contact!"));
  }
}

export function* deleteOtherContactSaga({ payload, onDelete, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const contactId = payload?.id ?? payload;
  const query = payload?.resident_id
    ? `?${QueryString.stringify({ resident_id: payload.resident_id })}`
    : "";
  try {
    const { data } = yield call(
      api(token).delete,
      `/api/resident-contact/${contactId}${query}`,
    );
    yield put(deleteOtherContactSuccess(data));
    typeof onDelete === "function" && onDelete(data);
  } catch (e) {
    yield put(deleteOtherContactFailure());
    typeof onError === "function" && onError(e);
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while deleting other contact!"));
  }
}

export function* removeContactSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { contactId, residentId, action = "auto" } = payload || {};

  try {
    const body = yield call(
      api(token).post,
      `/api/resident-contact/${contactId}/removal`,
      {
        resident_id: residentId,
        action,
      },
    );
    const result = body?.data;
    yield put(removeContactSuccess(result));

    if (!result?.requires_confirmation) {
      yield put(
        successAlert(
          body?.message ||
            (result?.action === "revoke"
              ? "Family App access revoked for this resident."
              : "Contact deleted successfully."),
        ),
      );
    }

    typeof onSuccess === "function" && onSuccess(result);
  } catch (e) {
    yield put(removeContactFailure());
    typeof onError === "function" && onError(e);
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else
      yield put(
        errorAlert(e?.message || "Error while processing contact removal."),
      );
  }
}

export function* searchContactsSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = payload ? QueryString.stringify(payload) : "";
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident-contact-search?${query}`,
    );
    yield put(searchContactsSuccess(data));
  } catch (e) {
    yield put(searchContactsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while searching contacts!"));
  }
}

export function* getContactSuggestionsSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = payload ? QueryString.stringify(payload) : "";
  try {
    const { data } = yield call(
      api(token).get,
      `/api/governance-item?${query}`,
    );
    yield put(getContactSuggestionsSuccess(data));
  } catch (e) {
    yield put(getContactSuggestionsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while getting contact suggestions!"));
  }
}

export function* sendFamilyInviteSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { contactId, role, channel, residentId } = payload || {};
  try {
    const body = yield call(
      api(token).post,
      `/api/resident-contact/${contactId}/family-invite`,
      { role, channel: channel || "email", resident_id: residentId },
    );
    yield put(sendFamilyInviteSuccess(body?.data));
    yield put(
      successAlert(
        body?.message ||
          (body?.data?.granted
            ? "Family Connect access granted for this resident."
            : "Family Connect invite sent."),
      ),
    );
    yield put(toggleFamilyInviteModal({ open: false }));
    typeof onSuccess === "function" && onSuccess(body?.data);
  } catch (e) {
    yield put(sendFamilyInviteFailure());
    typeof onError === "function" && onError(e);
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while sending Family Connect invite."));
  }
}

export function* resendFamilyInviteSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { contactId, role, channel, residentId } = payload || {};
  try {
    const body = yield call(
      api(token).post,
      `/api/resident-contact/${contactId}/family-invite/resend`,
      { role, channel: channel || "email", resident_id: residentId },
    );
    yield put(resendFamilyInviteSuccess(body?.data));
    yield put(
      successAlert(
        body?.message ||
          (body?.data?.granted
            ? "Family Connect access granted for this resident."
            : "Family Connect invite resent."),
      ),
    );
    yield put(toggleFamilyInviteModal({ open: false }));
    typeof onSuccess === "function" && onSuccess(body?.data);
  } catch (e) {
    yield put(resendFamilyInviteFailure());
    typeof onError === "function" && onError(e);
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while resending Family Connect invite."));
  }
}

export function* revokeFamilyAccessSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const contactId = payload?.contactId || payload;
  const residentId = payload?.residentId;
  try {
    const query = residentId
      ? `?${QueryString.stringify({ resident_id: residentId })}`
      : "";
    const body = yield call(
      api(token).post,
      `/api/resident-contact/${contactId}/family-access/revoke${query}`,
    );
    yield put(revokeFamilyAccessSuccess(body?.data));
    yield put(
      successAlert(
        body?.message || "Family Connect access revoked for this resident.",
      ),
    );
    yield put(toggleFamilyInviteModal({ open: false }));
    typeof onSuccess === "function" && onSuccess(body?.data);
  } catch (e) {
    yield put(revokeFamilyAccessFailure());
    typeof onError === "function" && onError(e);
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while revoking Family Connect access."));
  }
}

function* fetchOneFamilyAccess(token, contactId, residentId) {
  try {
    const query = residentId
      ? `?${QueryString.stringify({ resident_id: residentId })}`
      : "";
    const body = yield call(
      api(token).get,
      `/api/resident-contact/${contactId}/family-access${query}`,
    );
    return body?.data || null;
  } catch (e) {
    return null;
  }
}

/** payload: contactId | { contactId, residentId } | { contactIds, residentId } */
export function* fetchFamilyAccessSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const residentId = payload?.residentId;
  const contactIds = Array.isArray(payload?.contactIds)
    ? payload.contactIds.filter(Boolean)
    : [payload?.contactId || payload].filter(Boolean);

  if (contactIds.length === 0) {
    yield put(fetchFamilyAccessFailure());
    return;
  }

  try {
    const results = yield all(
      contactIds.map((id) => call(fetchOneFamilyAccess, token, id, residentId)),
    );
    const entries = results.filter(Boolean);
    yield put(fetchFamilyAccessSuccess(entries));
  } catch (e) {
    yield put(fetchFamilyAccessFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching Family Connect status."));
  }
}

export function* fetchFamilyModulePermissionsSaga({
  payload,
  onSuccess,
  onError,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { contactId, residentId } = payload || {};
  try {
    const query = QueryString.stringify({ resident_id: residentId });
    const body = yield call(
      api(token).get,
      `/api/resident-contact/${contactId}/family-access/modules?${query}`,
    );
    yield put(fetchFamilyModulePermissionsSuccess(body?.data));
    typeof onSuccess === "function" && onSuccess(body?.data);
  } catch (e) {
    yield put(fetchFamilyModulePermissionsFailure());
    typeof onError === "function" && onError(e);
    yield put(errorAlert("Error while fetching Family App permissions."));
  }
}

export function* saveFamilyModuleOverridesSaga({
  payload,
  onSuccess,
  onError,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { contactId, residentId, moduleOverrides } = payload || {};
  try {
    const body = yield call(
      api(token).post,
      `/api/resident-contact/${contactId}/family-access/modules`,
      {
        resident_id: residentId,
        module_overrides: moduleOverrides,
      },
    );
    yield put(saveFamilyModuleOverridesSuccess(body?.data));
    yield put(successAlert("Family App permissions updated."));
    typeof onSuccess === "function" && onSuccess(body?.data);
  } catch (e) {
    yield put(saveFamilyModuleOverridesFailure());
    typeof onError === "function" && onError(e);
    const detail = typeof e === "string" ? e : e?.message || e?.error || null;
    yield put(
      errorAlert(
        detail
          ? `Error while saving Family App permissions: ${detail}`
          : "Error while saving Family App permissions.",
      ),
    );
  }
}

export function* fetchNextOfKinFlow() {
  yield takeEvery(GET_NEXT_OF_KIN, fetchNextOfKinSaga);
}

export function* saveNextOfKinFlow() {
  yield takeEvery(SAVE_NEXT_OF_KIN, saveNextOfKinSaga);
}
export function* fetchOtherContactsFlow() {
  yield takeEvery(FETCH_OTHER_CONTACTS, fetchOtherContactsSaga);
}
export function* addOtherContactFlow() {
  yield takeEvery(ADD_OTHER_CONTACT, addOtherContactSaga);
}
export function* editOtherContactFlow() {
  yield takeEvery(EDIT_OTHER_CONTACT, editOtherContactSaga);
}
export function* deleteOtherContactFlow() {
  yield takeEvery(DELETE_OTHER_CONTACT, deleteOtherContactSaga);
}
export function* removeContactFlow() {
  yield takeEvery(REMOVE_CONTACT, removeContactSaga);
}
export function* searchContactsFlow() {
  yield takeEvery(SEARCH_CONTACTS, searchContactsSaga);
}
export function* getContactSuggestionsFlow() {
  yield takeEvery(GET_CONTACT_SUGGESTIONS, getContactSuggestionsSaga);
}
export function* sendFamilyInviteFlow() {
  yield takeEvery(SEND_FAMILY_INVITE, sendFamilyInviteSaga);
}
export function* resendFamilyInviteFlow() {
  yield takeEvery(RESEND_FAMILY_INVITE, resendFamilyInviteSaga);
}
export function* revokeFamilyAccessFlow() {
  yield takeEvery(REVOKE_FAMILY_ACCESS, revokeFamilyAccessSaga);
}
export function* fetchFamilyAccessFlow() {
  yield takeEvery(FETCH_FAMILY_ACCESS, fetchFamilyAccessSaga);
}
export function* fetchFamilyModulePermissionsFlow() {
  yield takeEvery(
    FETCH_FAMILY_MODULE_PERMISSIONS,
    fetchFamilyModulePermissionsSaga,
  );
}
export function* saveFamilyModuleOverridesFlow() {
  yield takeEvery(SAVE_FAMILY_MODULE_OVERRIDES, saveFamilyModuleOverridesSaga);
}

export default function* ResidentNetworkSaga() {
  yield all([
    fetchNextOfKinFlow(),
    saveNextOfKinFlow(),
    fetchOtherContactsFlow(),
    addOtherContactFlow(),
    editOtherContactFlow(),
    deleteOtherContactFlow(),
    removeContactFlow(),
    searchContactsFlow(),
    getContactSuggestionsFlow(),
    sendFamilyInviteFlow(),
    resendFamilyInviteFlow(),
    revokeFamilyAccessFlow(),
    fetchFamilyAccessFlow(),
    fetchFamilyModulePermissionsFlow(),
    saveFamilyModuleOverridesFlow(),
  ]);
}
